import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dossiersAPI, gestionnairesAPI, tablesAPI, agircAPI } from '../services/api';
import { Check, ChevronRight, ChevronLeft, Loader2, AlertTriangle, Plus, Trash2 } from 'lucide-react';

const NouveauDossier = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [caisses, setCaisses] = useState([]);
  const [gestionnaires, setGestionnaires] = useState([]);
  const [searchingCaisse, setSearchingCaisse] = useState(false);
  const [caisseNotification, setCaisseNotification] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    // Étape 1 : Identité
    caisse: '',
    nom: '',
    prenom: '',
    nom_usage: '',
    date_naissance: '',
    nss: '',
    cle_nss: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'France',
    telephone: '',
    telephone_fixe: '',
    email: '',
    
    // Étape 2 : Situation
    type_logement: '',
    statut_occupation: '', // Propriétaire, Locataire, Hébergé
    situation_familiale: '',
    date_situation_familiale: '',
    // Conjoint
    conjoint_nom: '',
    conjoint_prenom: '',
    conjoint_date_naissance: '',
    conjoint_nss: '',
    conjoint_cle_nss: '',
    // Situation pro
    situation_pro_demandeur: '',
    date_situation_pro_demandeur: '',
    situation_pro_conjoint: '',
    date_situation_pro_conjoint: '',
    // Autres personnes foyer
    autres_personnes_foyer: [],
    
    // Étape 3 : Ressources
    salaires: 0,
    indemnites_chomage: 0,
    indemnites_journalieres: 0,
    pension_invalidite_ss: 0,
    pension_invalidite_compl: 0,
    rente_accident_travail: 0,
    retraite_base: 0,
    retraite_base_type: '',
    retraite_msa: 0,
    retraite_autres_regimes: 0,
    retraite_autres_regimes_detail: '',
    retraite_complementaire: 0,
    retraite_complementaire_caisse: '',
    retraite_complementaire_type: '',
    allocations_familiales: 0,
    allocation_logement: 0,
    rsa_prime_activite: 0,
    aah_aeeh_pch: 0,
    apa_actp: 0,
    autres_prestations: 0,
    autres_prestations_detail: '',
    autres_revenus: 0,
    autres_revenus_detail: '',
    ressources_conjoint: 0,
    ressources_conjoint_detail: '',
    
    // Étape 4 : Charges & Finances
    loyer: 0,
    copropriete: 0,
    taxe_fonciere: 0,
    impot_revenu: 0,
    assurance_habitation: 0,
    assurance_vehicule: 0,
    energie: 0,
    telephonie: 0,
    eau: 0,
    frais_hebergement: 0,
    frais_maintien_domicile: 0,
    cotisations_mutuelle: 0,
    autres_charges: 0,
    autres_charges_detail: '',
    // Épargne
    epargne_montant: 0,
    // Surendettement
    surendettement_commission: false,
    surendettement_plan: false,
    surendettement_mensualite: 0,
    // Crédits
    credits: [],
    
    // Étape 5 : Contexte & Motif
    date_evenement: '',
    montant_factures: 0,
    premiere_demande: true,
    motif: '',
    priorite: 'med',
    gestionnaire_id: '',
    source: 'conseiller',
    note_interne: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Recherche automatique de la caisse AGIRC-ARRCO
  const rechercherCaisseAgirc = useCallback(async () => {
    const { nom, prenom, nss, cle_nss } = formData;
    if (!nom || !prenom || nss.length !== 13 || cle_nss.length !== 2) return;
    
    setSearchingCaisse(true);
    setCaisseNotification(null);
    
    try {
      const res = await agircAPI.rechercheCaisse({ nom, prenom, nss, cle_nss });
      
      if (res.data.success) {
        setFormData(prev => ({ ...prev, caisse: res.data.caisse }));
        if (res.data.is_new_caisse) {
          await loadData();
          setCaisseNotification({ type: 'new', message: `Nouvelle caisse ajoutée : ${res.data.caisse}` });
        } else {
          setCaisseNotification({ type: 'found', message: `Caisse trouvée : ${res.data.caisse}` });
        }
      } else {
        setCaisseNotification({ type: 'error', message: res.data.error || 'Caisse non trouvée' });
      }
    } catch (error) {
      setCaisseNotification({ type: 'error', message: 'Erreur lors de la recherche automatique' });
    } finally {
      setSearchingCaisse(false);
    }
  }, [formData.nom, formData.prenom, formData.nss, formData.cle_nss]);

  useEffect(() => {
    const { nom, prenom, nss, cle_nss, caisse } = formData;
    if (caisse) return;
    if (nom && prenom && nss.length === 13 && cle_nss.length === 2) {
      const timer = setTimeout(() => rechercherCaisseAgirc(), 1000);
      return () => clearTimeout(timer);
    }
  }, [formData.nom, formData.prenom, formData.nss, formData.cle_nss, rechercherCaisseAgirc]);

  const loadData = async () => {
    try {
      const [caissesRes, gestRes] = await Promise.all([
        tablesAPI.getCaisses(),
        gestionnairesAPI.getAll()
      ]);
      setCaisses(caissesRes.data);
      setGestionnaires(gestRes.data);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const steps = [
    { title: 'Identité', icon: '👤' },
    { title: 'Situation', icon: '🏠' },
    { title: 'Ressources', icon: '💰' },
    { title: 'Charges', icon: '📊' },
    { title: 'Motif', icon: '📝' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  // Gestion des personnes du foyer
  const addPersonneFoyer = () => {
    setFormData(prev => ({
      ...prev,
      autres_personnes_foyer: [...prev.autres_personnes_foyer, { nom: '', prenom: '', date_naissance: '', lien_parente: '', profession: '' }]
    }));
  };

  const updatePersonneFoyer = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.autres_personnes_foyer];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, autres_personnes_foyer: updated };
    });
  };

  const removePersonneFoyer = (index) => {
    setFormData(prev => ({
      ...prev,
      autres_personnes_foyer: prev.autres_personnes_foyer.filter((_, i) => i !== index)
    }));
  };

  // Gestion des crédits
  const addCredit = () => {
    setFormData(prev => ({
      ...prev,
      credits: [...prev.credits, { organisme: '', motif: '', montant_pret: 0, date_debut: '', date_fin: '', mensualite: 0 }]
    }));
  };

  const updateCredit = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.credits];
      updated[index] = { ...updated[index], [field]: field === 'montant_pret' || field === 'mensualite' ? parseFloat(value) || 0 : value };
      return { ...prev, credits: updated };
    });
  };

  const removeCredit = (index) => {
    setFormData(prev => ({
      ...prev,
      credits: prev.credits.filter((_, i) => i !== index)
    }));
  };

  const validateStep = () => {
    if (currentStep === 0) {
      // Caisse n'est plus obligatoire
      return formData.nom && formData.prenom && formData.adresse && formData.telephone;
    }
    if (currentStep === 4) {
      return formData.motif;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep() && currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const showConjoint = ['Marié(e)', 'Pacsé(e)', 'Vie maritale'].includes(formData.situation_familiale);

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    
    try {
      const payload = {
        identite: {
          nom: formData.nom,
          prenom: formData.prenom,
          nom_usage: formData.nom_usage || null,
          date_naissance: formData.date_naissance || null,
          nss: formData.nss || null,
          cle_nss: formData.cle_nss || null,
          adresse: formData.adresse,
          code_postal: formData.code_postal,
          ville: formData.ville,
          pays: formData.pays,
          telephone: formData.telephone,
          telephone_fixe: formData.telephone_fixe || null,
          email: formData.email || null
        },
        situation: {
          type_logement: formData.type_logement || null,
          statut_occupation: formData.statut_occupation || null,
          situation_familiale: formData.situation_familiale || null,
          date_situation_familiale: formData.date_situation_familiale || null,
          conjoint: showConjoint ? {
            nom: formData.conjoint_nom || null,
            prenom: formData.conjoint_prenom || null,
            date_naissance: formData.conjoint_date_naissance || null,
            nss: formData.conjoint_nss || null,
            cle_nss: formData.conjoint_cle_nss || null
          } : null,
          autres_personnes_foyer: formData.autres_personnes_foyer.length > 0 ? formData.autres_personnes_foyer : null,
          situation_pro_demandeur: formData.situation_pro_demandeur || null,
          date_situation_pro_demandeur: formData.date_situation_pro_demandeur || null,
          situation_pro_conjoint: formData.situation_pro_conjoint || null,
          date_situation_pro_conjoint: formData.date_situation_pro_conjoint || null
        },
        ressources: {
          salaires: formData.salaires,
          indemnites_chomage: formData.indemnites_chomage,
          indemnites_journalieres: formData.indemnites_journalieres,
          pension_invalidite_ss: formData.pension_invalidite_ss,
          pension_invalidite_compl: formData.pension_invalidite_compl,
          rente_accident_travail: formData.rente_accident_travail,
          retraite_base: formData.retraite_base,
          retraite_base_type: formData.retraite_base_type || null,
          retraite_msa: formData.retraite_msa,
          retraite_autres_regimes: formData.retraite_autres_regimes,
          retraite_autres_regimes_detail: formData.retraite_autres_regimes_detail || null,
          retraite_complementaire: formData.retraite_complementaire,
          retraite_complementaire_caisse: formData.retraite_complementaire_caisse || null,
          retraite_complementaire_type: formData.retraite_complementaire_type || null,
          allocations_familiales: formData.allocations_familiales,
          allocation_logement: formData.allocation_logement,
          rsa_prime_activite: formData.rsa_prime_activite,
          aah_aeeh_pch: formData.aah_aeeh_pch,
          apa_actp: formData.apa_actp,
          autres_prestations: formData.autres_prestations,
          autres_prestations_detail: formData.autres_prestations_detail || null,
          autres_revenus: formData.autres_revenus,
          autres_revenus_detail: formData.autres_revenus_detail || null,
          ressources_conjoint: formData.ressources_conjoint,
          ressources_conjoint_detail: formData.ressources_conjoint_detail || null
        },
        charges: {
          loyer: formData.loyer,
          copropriete: formData.copropriete,
          taxe_fonciere: formData.taxe_fonciere,
          impot_revenu: formData.impot_revenu,
          assurance_habitation: formData.assurance_habitation,
          assurance_vehicule: formData.assurance_vehicule,
          energie: formData.energie,
          telephonie: formData.telephonie,
          eau: formData.eau,
          frais_hebergement: formData.frais_hebergement,
          frais_maintien_domicile: formData.frais_maintien_domicile,
          cotisations_mutuelle: formData.cotisations_mutuelle,
          autres_charges: formData.autres_charges,
          autres_charges_detail: formData.autres_charges_detail || null
        },
        epargne: { montant: formData.epargne_montant },
        credits: formData.credits.length > 0 ? formData.credits : null,
        surendettement: {
          commission_saisie: formData.surendettement_commission,
          plan_en_cours: formData.surendettement_plan,
          plan_mensualite: formData.surendettement_mensualite
        },
        contexte_demande: {
          date_evenement: formData.date_evenement || null,
          montant_factures: formData.montant_factures,
          premiere_demande: formData.premiere_demande,
          raisons: formData.motif
        },
        caisse: formData.caisse,
        motif: formData.motif,
        priorite: formData.priorite,
        gestionnaire_id: formData.gestionnaire_id || null,
        source: formData.source,
        note_interne: formData.note_interne || null
      };

      await dossiersAPI.create(payload);
      navigate('/dossiers');
    } catch (error) {
      console.error('Erreur création dossier:', error);
      alert('Erreur lors de la création du dossier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">➕ Nouvelle demande</h1>
          <p className="text-gray-600 mt-1">Saisie d'un nouveau dossier client</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold transition ${
                  index === currentStep ? 'bg-green-600 text-white' :
                  index < currentStep ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  {index < currentStep ? <Check className="w-6 h-6" /> : step.icon}
                </div>
                <div className={`text-sm mt-2 font-medium ${index === currentStep ? 'text-green-600' : 'text-gray-500'}`}>
                  {step.title}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded transition ${index < currentStep ? 'bg-green-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[400px]">
          {/* Étape 1 : Identité */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">💡 Les champs marqués d'un * sont obligatoires</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Caisse complémentaire <span className="text-gray-400 font-normal">(si connue)</span>
                  {searchingCaisse && <span className="ml-2 inline-flex items-center text-blue-600"><Loader2 className="w-4 h-4 animate-spin mr-1" />Recherche AGIRC-ARRCO...</span>}
                </label>
                <select name="caisse" value={formData.caisse} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="select-caisse">
                  <option value="">— Sélectionner une caisse —</option>
                  {caisses.map(c => <option key={c.id} value={c.nom}>{c.nom}</option>)}
                </select>
                {caisseNotification && (
                  <div className={`mt-2 p-3 rounded-lg text-sm flex items-start gap-2 ${
                    caisseNotification.type === 'new' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
                    caisseNotification.type === 'found' ? 'bg-green-50 border border-green-200 text-green-800' :
                    'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {caisseNotification.type === 'new' && <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    <span>{caisseNotification.message}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom de naissance <span className="text-red-600">*</span></label>
                  <input type="text" name="nom" value={formData.nom} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="NOM" data-testid="input-nom" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prénom <span className="text-red-600">*</span></label>
                  <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="input-prenom" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom d'usage</label>
                  <input type="text" name="nom_usage" value={formData.nom_usage} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Si différent" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de naissance</label>
                  <input type="date" name="date_naissance" value={formData.date_naissance} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">N° Sécurité sociale</label>
                  <input type="text" name="nss" value={formData.nss} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="13 chiffres" maxLength="13" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clé</label>
                  <input type="text" name="cle_nss" value={formData.cle_nss} onChange={(e) => setFormData({...formData, cle_nss: e.target.value.replace(/\D/g, '').slice(0, 2)})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-center font-semibold" placeholder="00" maxLength="2" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse <span className="text-red-600">*</span></label>
                <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="input-adresse" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                  <input type="text" name="code_postal" value={formData.code_postal} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" maxLength="5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                  <input type="text" name="ville" value={formData.ville} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                  <input type="text" name="pays" value={formData.pays} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone portable <span className="text-red-600">*</span></label>
                  <input type="tel" name="telephone" value={formData.telephone} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="input-telephone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone fixe</label>
                  <input type="tel" name="telephone_fixe" value={formData.telephone_fixe} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
            </div>
          )}

          {/* Étape 2 : Situation */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Logement */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Type de logement</label>
                  <div className="flex gap-4">
                    {['Maison', 'Appartement'].map(type => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="type_logement" value={type} checked={formData.type_logement === type} onChange={handleChange} className="w-4 h-4 text-green-600" />
                        <span>{type === 'Maison' ? '🏠' : '🏢'} {type}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Statut d'occupation</label>
                  <div className="flex gap-4">
                    {['Propriétaire', 'Locataire', 'Hébergé'].map(statut => (
                      <label key={statut} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="statut_occupation" value={statut} checked={formData.statut_occupation === statut} onChange={handleChange} className="w-4 h-4 text-green-600" />
                        <span className="text-sm">{statut}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Situation familiale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Situation familiale</label>
                <div className="grid grid-cols-4 gap-3">
                  {['Célibataire', 'Marié(e)', 'Pacsé(e)', 'Vie maritale', 'Divorcé(e)', 'Séparé(e)', 'Veuf(ve)'].map(sit => (
                    <label key={sit} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="situation_familiale" value={sit} checked={formData.situation_familiale === sit} onChange={handleChange} className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{sit}</span>
                    </label>
                  ))}
                </div>
                {formData.situation_familiale && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Depuis le</label>
                    <input type="date" name="date_situation_familiale" value={formData.date_situation_familiale} onChange={handleChange} className="w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                  </div>
                )}
              </div>

              {/* Conjoint */}
              {showConjoint && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-gray-900">👫 Informations conjoint</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                      <input type="text" name="conjoint_nom" value={formData.conjoint_nom} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                      <input type="text" name="conjoint_prenom" value={formData.conjoint_prenom} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date de naissance</label>
                      <input type="date" name="conjoint_date_naissance" value={formData.conjoint_date_naissance} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">N° Sécu conjoint</label>
                        <input type="text" name="conjoint_nss" value={formData.conjoint_nss} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" maxLength="13" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Clé</label>
                        <input type="text" name="conjoint_cle_nss" value={formData.conjoint_cle_nss} onChange={(e) => setFormData({...formData, conjoint_cle_nss: e.target.value.replace(/\D/g, '').slice(0, 2)})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-center" maxLength="2" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Situation professionnelle */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Situation professionnelle demandeur</label>
                  <select name="situation_pro_demandeur" value={formData.situation_pro_demandeur} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option value="">— Sélectionner —</option>
                    {['Retraité(e)', 'Salarié(e)', "Demandeur d'emploi", 'En arrêt maladie', 'En invalidité', 'Autre'].map(sit => (
                      <option key={sit} value={sit}>{sit}</option>
                    ))}
                  </select>
                  {formData.situation_pro_demandeur && (
                    <div className="mt-2">
                      <label className="block text-sm text-gray-600 mb-1">Depuis le</label>
                      <input type="date" name="date_situation_pro_demandeur" value={formData.date_situation_pro_demandeur} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                    </div>
                  )}
                </div>
                {showConjoint && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Situation professionnelle conjoint</label>
                    <select name="situation_pro_conjoint" value={formData.situation_pro_conjoint} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                      <option value="">— Sélectionner —</option>
                      {['Retraité(e)', 'Salarié(e)', "Demandeur d'emploi", 'En arrêt maladie', 'En invalidité', 'Autre'].map(sit => (
                        <option key={sit} value={sit}>{sit}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Autres personnes du foyer */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Autres personnes du foyer</label>
                  <button type="button" onClick={addPersonneFoyer} className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Ajouter
                  </button>
                </div>
                {formData.autres_personnes_foyer.map((personne, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium text-gray-600">Personne {index + 1}</span>
                      <button type="button" onClick={() => removePersonneFoyer(index)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <input type="text" placeholder="Nom" value={personne.nom} onChange={(e) => updatePersonneFoyer(index, 'nom', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="text" placeholder="Prénom" value={personne.prenom} onChange={(e) => updatePersonneFoyer(index, 'prenom', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <select value={personne.lien_parente} onChange={(e) => updatePersonneFoyer(index, 'lien_parente', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="">Lien de parenté</option>
                        <option value="Enfant">Enfant</option>
                        <option value="Parent">Parent</option>
                        <option value="Autre">Autre</option>
                      </select>
                      <input type="text" placeholder="Profession/situation" value={personne.profession} onChange={(e) => updatePersonneFoyer(index, 'profession', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Étape 3 : Ressources */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">💰 Indiquez les montants mensuels nets</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Salaires & assimilés</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Salaires</label>
                    <input type="number" name="salaires" value={formData.salaires} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Indemnités chômage</label>
                    <input type="number" name="indemnites_chomage" value={formData.indemnites_chomage} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Indemnités journalières</label>
                    <input type="number" name="indemnites_journalieres" value={formData.indemnites_journalieres} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invalidité & Accident</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pension invalidité SS</label>
                    <input type="number" name="pension_invalidite_ss" value={formData.pension_invalidite_ss} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pension invalidité complémentaire</label>
                    <input type="number" name="pension_invalidite_compl" value={formData.pension_invalidite_compl} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rente accident travail</label>
                    <input type="number" name="rente_accident_travail" value={formData.rente_accident_travail} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Retraites de base</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Retraite CNAV/CARSAT</label>
                    <input type="number" name="retraite_base" value={formData.retraite_base} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type (D/R)</label>
                    <select name="retraite_base_type" value={formData.retraite_base_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                      <option value="">—</option>
                      <option value="D">D - Directe</option>
                      <option value="R">R - Réversion</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Retraite MSA</label>
                    <input type="number" name="retraite_msa" value={formData.retraite_msa} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Autres régimes</label>
                    <input type="number" name="retraite_autres_regimes" value={formData.retraite_autres_regimes} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Détail autres régimes</label>
                    <input type="text" name="retraite_autres_regimes_detail" value={formData.retraite_autres_regimes_detail} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="RSI, IRCANTEC..." />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Retraites complémentaires</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Montant mensuel</label>
                    <input type="number" name="retraite_complementaire" value={formData.retraite_complementaire} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Caisse</label>
                    <input type="text" name="retraite_complementaire_caisse" value={formData.retraite_complementaire_caisse} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="Agirc-Arrco..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type (D/R)</label>
                    <select name="retraite_complementaire_type" value={formData.retraite_complementaire_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                      <option value="">—</option>
                      <option value="D">D - Directe</option>
                      <option value="R">R - Réversion</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Prestations sociales</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Allocations familiales</label>
                    <input type="number" name="allocations_familiales" value={formData.allocations_familiales} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Allocation logement (APL, AL)</label>
                    <input type="number" name="allocation_logement" value={formData.allocation_logement} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">RSA / Prime d'activité</label>
                    <input type="number" name="rsa_prime_activite" value={formData.rsa_prime_activite} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AAH / AEEH / PCH</label>
                    <input type="number" name="aah_aeeh_pch" value={formData.aah_aeeh_pch} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">APA / ACTP (autonomie)</label>
                    <input type="number" name="apa_actp" value={formData.apa_actp} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Autres prestations</label>
                    <input type="number" name="autres_prestations" value={formData.autres_prestations} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Détail autres prestations</label>
                  <input type="text" name="autres_prestations_detail" value={formData.autres_prestations_detail} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="ASPA, ASI..." />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Autres ressources</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Autres revenus</label>
                    <input type="number" name="autres_revenus" value={formData.autres_revenus} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Précisions</label>
                    <input type="text" name="autres_revenus_detail" value={formData.autres_revenus_detail} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="Pensions alimentaires reçues, revenus fonciers..." />
                  </div>
                </div>
                {showConjoint && (
                  <div className="mt-4 grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ressources du conjoint (total)</label>
                      <input type="number" name="ressources_conjoint" value={formData.ressources_conjoint} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Détail conjoint</label>
                      <input type="text" name="ressources_conjoint_detail" value={formData.ressources_conjoint_detail} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="Salaire, retraite..." />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Étape 4 : Charges */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Charges mensuelles (€)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Loyer (avant APL)</label>
                    <input type="number" name="loyer" value={formData.loyer} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Copropriété</label>
                    <input type="number" name="copropriete" value={formData.copropriete} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Taxe foncière</label>
                    <input type="number" name="taxe_fonciere" value={formData.taxe_fonciere} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Impôt sur le revenu</label>
                    <input type="number" name="impot_revenu" value={formData.impot_revenu} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assurance habitation</label>
                    <input type="number" name="assurance_habitation" value={formData.assurance_habitation} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assurance véhicule</label>
                    <input type="number" name="assurance_vehicule" value={formData.assurance_vehicule} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Énergie</label>
                    <input type="number" name="energie" value={formData.energie} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphonie/Internet</label>
                    <input type="number" name="telephonie" value={formData.telephonie} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Eau</label>
                    <input type="number" name="eau" value={formData.eau} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mutuelle/Prévoyance</label>
                    <input type="number" name="cotisations_mutuelle" value={formData.cotisations_mutuelle} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frais hébergement</label>
                    <input type="number" name="frais_hebergement" value={formData.frais_hebergement} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Aide à domicile</label>
                    <input type="number" name="frais_maintien_domicile" value={formData.frais_maintien_domicile} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                  </div>
                </div>
              </div>

              {/* Épargne */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 Épargne disponible</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Montant global de l'épargne</label>
                  <input type="number" name="epargne_montant" value={formData.epargne_montant} onChange={handleChange} className="w-48 px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                </div>
              </div>

              {/* Surendettement */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">⚠️ Surendettement</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="surendettement_commission" checked={formData.surendettement_commission} onChange={handleChange} className="w-4 h-4 text-green-600 rounded" />
                    <span>Commission de surendettement saisie</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="surendettement_plan" checked={formData.surendettement_plan} onChange={handleChange} className="w-4 h-4 text-green-600 rounded" />
                    <span>Plan de surendettement en cours</span>
                  </label>
                  {formData.surendettement_plan && (
                    <div className="ml-7">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mensualité du plan</label>
                      <input type="number" name="surendettement_mensualite" value={formData.surendettement_mensualite} onChange={handleChange} className="w-32 px-4 py-2 border border-gray-300 rounded-lg" step="0.01" />
                    </div>
                  )}
                </div>
              </div>

              {/* Crédits */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">💳 Crédits en cours</h3>
                  <button type="button" onClick={addCredit} className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Ajouter un crédit
                  </button>
                </div>
                {formData.credits.map((credit, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium text-gray-600">Crédit {index + 1}</span>
                      <button type="button" onClick={() => removeCredit(index)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <input type="text" placeholder="Organisme" value={credit.organisme} onChange={(e) => updateCredit(index, 'organisme', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="text" placeholder="Motif" value={credit.motif} onChange={(e) => updateCredit(index, 'motif', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="number" placeholder="Mensualité €" value={credit.mensualite} onChange={(e) => updateCredit(index, 'mensualite', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" step="0.01" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Étape 5 : Motif */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">⚠️ Ne pas mentionner d'informations liées à l'état de santé</p>
              </div>

              {/* Contexte de la demande */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de l'événement déclencheur</label>
                  <input type="date" name="date_evenement" value={formData.date_evenement} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Montant total des factures</label>
                  <input type="number" name="montant_factures" value={formData.montant_factures} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Première demande d'aide ?</label>
                  <div className="flex gap-4 mt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="premiere_demande" value="true" checked={formData.premiere_demande === true} onChange={() => setFormData({...formData, premiere_demande: true})} className="w-4 h-4 text-green-600" />
                      <span>Oui</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="premiere_demande" value="false" checked={formData.premiere_demande === false} onChange={() => setFormData({...formData, premiere_demande: false})} className="w-4 h-4 text-green-600" />
                      <span>Non</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Motif de la demande <span className="text-red-600">*</span></label>
                <textarea name="motif" value={formData.motif} onChange={handleChange} rows="4" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Décrivez le motif de la demande et l'événement imprévu..." data-testid="textarea-motif" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                  <select name="priorite" value={formData.priorite} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option value="low">🟢 Basse</option>
                    <option value="med">🟠 Moyenne</option>
                    <option value="high">🔴 Haute</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigner à</label>
                  <select name="gestionnaire_id" value={formData.gestionnaire_id} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option value="">— Non assigné —</option>
                    {gestionnaires.map(g => <option key={g.id} value={g.id}>{g.prenom} {g.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
                  <select name="source" value={formData.source} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option value="conseiller">Conseiller</option>
                    <option value="telephone">Téléphone</option>
                    <option value="courrier">Courrier</option>
                    <option value="lien">Lien client</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note interne</label>
                <textarea name="note_interne" value={formData.note_interne} onChange={handleChange} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Observations internes..." />
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          <button onClick={prevStep} disabled={currentStep === 0} className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <ChevronLeft className="w-5 h-5" />
            Retour
          </button>
          
          {currentStep < steps.length - 1 ? (
            <button onClick={nextStep} disabled={!validateStep()} className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-testid="btn-next-step">
              Suivant
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading || !validateStep()} className="px-8 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-testid="btn-submit-dossier">
              {loading ? (
                <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Création...</>
              ) : (
                <><Check className="w-5 h-5" />Créer le dossier</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NouveauDossier;
